import { Table, type ITableProps, kaReducer } from 'ka-table';
import {
	deselectAllFilteredRows,
	deselectAllRows,
	deselectRow,
	selectAllFilteredRows,
	selectRow,
	selectRowsRange,
	openEditor,
	updateCellValue,
	closeEditor,
} from 'ka-table/actionCreators';
import { SortingMode, SortDirection } from 'ka-table/enums';
import { kaPropsUtils } from 'ka-table/utils';
import debounce from 'lodash/debounce';
import orderBy from 'lodash/orderBy';
import uniqueId from 'lodash/uniqueId';
import React, { useEffect, Fragment, useState } from 'react';
import { Image, Form } from 'react-bootstrap';
import {
	BsSortDown,
	BsSortUp,
	BsHouseHeartFill,
	BsHouse,
} from 'react-icons/bs';

import BayBreadcrumbs from '../BayBreadcrumbs';
import BayBulkActions from '../BayBulkActions';
import BayDataGridActionsBar from '../BayDataGridActionsBar';
import BayFilesProgressModal from '../BayFilesProgressModal';
import BayFilesUpload from '../BayFilesUpload';
import BaySearch from '../BaySearch';
import BaySpinner from '../BaySpinner';
import BayTooltip from '../BayTooltip';
import VisibilityIcon from '../Icons/VisibilityIcon';
import {
	getBytes,
	downloadURI,
	getFileColumnDataType,
	isPreviewFile,
	isThumbnailFile,
} from '../utilities/constants/filesConstants';
import {
	getIconByFileType,
	getFileType,
} from '../utilities/fileExtensionHandler';

import CellEditor from './components/CellEditor';
import CreateNewFolderModal from './components/CreateNewFolderModal';
import DeleteEntryModal from './components/DeleteEntryModal';
import DeleteThumbnailModal from './components/DeleteThumbnailModal';
import EditCell from './components/EditCell';
import EditEntryModal from './components/EditEntryModal';
import EditThumbnailModal from './components/EditThumbnailModal';
import FilesGridCardView from './components/FilesGridCardView';
import FilterDrawer from './components/FilterDrawer';
import MoveEntryModal from './components/MoveEntryModal';
import PreviewFileModal from './components/PreviewFileModal';

import './BayFilesGrid.scss';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// Extend ka-table's ITableProps with custom properties
interface ExtendedTableProps extends ITableProps {
	searchView?: boolean;
}

interface BayFilesGridProps {
	t: (_key: string) => string;
	section?: string;
	tableProps: ExtendedTableProps;
	isLoading?: boolean;
	changeTableProps: (_updater: (_prevState: ExtendedTableProps) => ExtendedTableProps) => void;
	onRowClick?: (_rowData: any) => void;
	onPreview?: (_rowData: any) => void;
	onEntryMove?: (_entries: any[]) => void;
	onEntryDelete?: (_entries: any[]) => void;
	onEntryCopy?: (_entries: any[]) => void;
	onEntryRename?: (_params: any) => Promise<void>;
	onEditFolder?: (_entry: any) => void;
	onSetVisibility?: (_entries: any[], _action?: string) => void;
	noDataChildren?: React.ReactNode;
	onDrop?: (_files: File[]) => void;
	uploadingPopUpTitle?: string;
	allowedFileTypes?: any;
	allowedFileTypesText?: string;
	allowedFileTypesErrorText?: string;
	showCreateFolderModal?: boolean;
	showEditFolderModal?: boolean;
	editFolderData?: any;
	showPreviewFileModal?: any;
	showDeleteModal?: boolean;
	showMoveModal?: boolean;
	showUploadFileModal?: boolean;
	uploadingFiles?: any[];
	selectedEntriesArray?: any[];
	setSelectedEntriesArray?: React.Dispatch<React.SetStateAction<any[]>>;
	setShowPreviewFileModal?: (_show: boolean | any) => void;
	setShowDeleteModal?: (_show: boolean, _entries?: any[]) => void;
	setShowMoveModal?: (_show: boolean) => void;
	setShowUploadFileModal?: (_show: boolean) => void;
	setCreateFolderModal?: (_show: boolean) => void;
	setShowEditFolderModal?: (_show: boolean) => void;
	setEditEntryModal?: (_show: boolean) => void;
	showEditEntryModal?: boolean;
	createFolderSubmitFunc?: (_data: any) => void;
	editFolderSubmitFunc?: (_data: any) => void;
	dataArray?: any[];
	breadCrumbsArray?: any[];
	handleBreadcrumbClick?: (_breadcrumb: any) => void;
	handleSearch?: (_value: string) => void;
	searchValue?: string;
	bulkActions?: any[];
	handleBulkAction?: (_action: string) => void;
	setIsSwitchOn?: (_value: boolean) => void;
	multiFileUpload?: boolean;
	deleteTitleText?: string;
	deleteBodyText?: string;
	formDataObjectName?: string;
	cardActionFunc?: (_event: any, _data: any) => void;
	editEntrySubmitFunc?: (_dataObj: any) => void;
	getEntries?: (_params: any) => void;
	isActionLoading?: boolean;
	isCopy?: boolean;
	currentEntry?: any;
	moveModalSubmitFunc?: (_params: any) => void;
	isReadyToShare?: boolean;
	disableUpload?: boolean;
	disableEdit?: boolean;
	apiConfig?: any;
	isSwitchOn?: boolean;
	showFilterButton?: boolean;
	showFilterDrawer?: boolean;
	setShowFilterDrawer?: (_show: boolean) => void;
	handleFilterSubmit?: (_data: any) => void;
	filterFileTypes?: any[];
	thumbnailFiles?: any[];
	onThumbnailEdit?: (_params: any) => void;
	onThumbnailDelete?: (_params: any) => void;
	showEditThumbnailModal?: any;
	setShowEditThumbnailModal?: (_modal: any) => void;
	filterCount?: number;
	shouldGenerateThumbnails?: boolean;
	enforceVideoConstraints?: boolean;
}

const BayFilesGrid: React.FC<BayFilesGridProps> = (props) => {
	const {
		t,
		section,
		tableProps,
		isLoading = false,
		changeTableProps,
		onRowClick = () => {},
		onPreview = () => {},
		onEntryMove = () => {},
		onEntryDelete = () => {},
		onEntryCopy = () => {},
		onEntryRename,
		onEditFolder = () => {},
		onSetVisibility = () => {},
		noDataChildren,
		onDrop,
		uploadingPopUpTitle,
		allowedFileTypes,
		allowedFileTypesText,
		allowedFileTypesErrorText,
		showCreateFolderModal = false,
		showEditFolderModal = false,
		editFolderData,
		showPreviewFileModal,
		showDeleteModal = false,
		showMoveModal = false,
		showUploadFileModal = false,
		uploadingFiles,
		selectedEntriesArray = [],
		setSelectedEntriesArray = () => {},
		setShowPreviewFileModal = () => {},
		setShowDeleteModal = () => {},
		setShowMoveModal = () => {},
		setShowUploadFileModal = () => {},
		setCreateFolderModal = () => {},
		setShowEditFolderModal = () => {},
		setEditEntryModal = () => {},
		showEditEntryModal = false,
		createFolderSubmitFunc = () => {},
		editFolderSubmitFunc = () => {},
		dataArray = [],
		breadCrumbsArray = [],
		handleBreadcrumbClick = () => {},
		handleSearch = () => {},
		searchValue = '',
		bulkActions = [],
		handleBulkAction = () => {},
		setIsSwitchOn = () => {},
		multiFileUpload,
		deleteTitleText,
		deleteBodyText,
		formDataObjectName,
		cardActionFunc = () => {},
		editEntrySubmitFunc = () => {},
		getEntries = () => {},
		isActionLoading = false,
		isCopy = false,
		currentEntry,
		moveModalSubmitFunc = () => {},
		isReadyToShare = false,
		disableUpload = false,
		disableEdit = false,
		apiConfig,
		isSwitchOn = false,
		showFilterButton = false,
		showFilterDrawer = false,
		setShowFilterDrawer= () => {},
		handleFilterSubmit = () => {},
		filterFileTypes = [],
		thumbnailFiles,
		onThumbnailEdit = () => {},
		onThumbnailDelete = () => {},
		showEditThumbnailModal,
		setShowEditThumbnailModal = () => {},
		filterCount = 0,
		shouldGenerateThumbnails = false,
		enforceVideoConstraints = false,
	} = props;

	const SELECTION_COLUMN_ID = 'selection-cell';

	// Add state for delete thumbnail modal
	const [showDeleteThumbnailModal, setShowDeleteThumbnailModal] =
		useState<{ show: boolean; rowData: any } | false>(false);
	const [thumbnailToDelete, setThumbnailToDelete] = useState<string | null>(null);

	useEffect(() => {
		if (!isLoading) {
			hideBulkActions();
		}
	}, [isLoading]);

	const dispatch = (action: any) => {
		changeTableProps((prevState: ExtendedTableProps) => {
			return kaReducer(prevState, action);
		});

		// NOTE: 'selectedEntriesArray' is used to store the selected files/folders.
		// This should be an empty array or filled with objects.
		if (tableProps?.data?.length > 0)
			switch (action.type) {
			case 'SelectRow':
				setSelectedEntriesArray((prevState: any[]) => {
					// NOTE: Order of selection is not maintained.
					// It is always the last selected row/item that is added to the selectedEntriesArray
					prevState = prevState?.filter(
						(data) => data._id !== action.rowKeyValue,
					); // Remove the item if it already exists
					return [
						...prevState,
						tableProps.data.find((data: any) => data._id === action.rowKeyValue),
					];
				});
				break;
			case 'DeselectRow':
				setSelectedEntriesArray((prevState: any[]) => {
					return prevState?.filter((data) => data._id !== action.rowKeyValue);
				});
				break;
			case 'DeselectRowsRange':
				setSelectedEntriesArray((prevState: any[]) => {
					return [
						...(prevState?.splice(0, action.rowKeyValueFrom - 1) || []),
						...(prevState?.splice(action.rowKeyValueTo + 1) || []),
					];
				});
				break;
			case 'DeselectAllFilteredRows':
			case 'DeselectAllRows':
				setSelectedEntriesArray([]);
				break;
			case 'SelectAllFilteredRows':
				setSelectedEntriesArray(tableProps.data);
				break;
			case 'SelectRowsRange':
				setSelectedEntriesArray(
					tableProps.data.slice(action.rowKeyValueFrom, action.rowKeyValueTo),
				);
				break;
			default:
			}
	};

	const hideBulkActions = () => {
		dispatch(deselectAllRows());
	};

	const renderBreadcrumbs = (file: any, rowData: any) => {
		if (
			!file.cloudObject.objKey ||
			file.cloudObject.objKey === '' ||
			file._id !== rowData._id
		) {
			return null;
		}

		const renderBreadcrumbsArray: React.ReactNode[] = [];
		const pathArray = file?.cloudObject?.objKey?.split('/');

		renderBreadcrumbsArray.push(
			<BayTooltip key={uniqueId()} overlay="Brand Assets" placement="top">
				<span
					className="search-breadcrumb active"
					onClick={() =>
						handleBreadcrumbClick({
							name: 'My Brand Assets',
							path: '',
							depth: 0,
						})
					}
				>
					Brand Assets
				</span>
			</BayTooltip>,
		);

		pathArray?.map((pathName: string, index: number) => {
			if (pathName) {
				renderBreadcrumbsArray.push(
					<Fragment key={uniqueId()}>
						<span className="search-breadcrumb divider mx-2">/</span>
						<BayTooltip overlay={`${pathName}`} placement="top">
							<span
								className={`search-breadcrumb ${index === pathArray.length - 1 && file?._type !== 'Entry$Folder' ? '' : 'active'}`}
								onClick={() =>
									index === pathArray.length - 1 &&
									file?._type !== 'Entry$Folder'
										? void 0
										: handleBreadcrumbClick({
											path: pathArray.slice(0, index + 1).join('/'),
											name: pathName,
											depth: index + 1,
										})
								}
							>{`${pathName}`}</span>
						</BayTooltip>
					</Fragment>,
				);
			}
		});

		return renderBreadcrumbsArray;
	};

	const debounceSearch = debounce((valueLabel: string) => {
		handleSearch(valueLabel);
	}, 250);

	const SelectionCell = ({
		rowKeyValue,
		dispatch,
		isSelectedRow,
		selectedRows,
	}: any) => {
		return (
			<Form.Check
				checked={isSelectedRow}
				className="bay-checkbox ms-1 mt-0"
				id={`files_grid-select_row_checkbox_${rowKeyValue}`}
				type="checkbox"
				onChange={(event) => {
					if ((event.nativeEvent as MouseEvent).shiftKey) {
						dispatch(selectRowsRange(rowKeyValue, [...selectedRows].pop()));
					} else if (event.currentTarget.checked) {
						dispatch(selectRow(rowKeyValue));
					} else {
						dispatch(deselectRow(rowKeyValue));
					}
				}}
			/>
		);
	};

	const SelectionHeader = ({ dispatch, areAllRowsSelected }: any) => {
		return (
			<Form.Check
				checked={
					tableProps?.data?.length > 0 && areAllRowsSelected ? true : false
				}
				className="bay-checkbox ms-1 mt-0"
				disabled={isLoading || tableProps?.data?.length <= 0}
				id="files_grid-select_all_checkbox"
				type="checkbox"
				onChange={(event) => {
					if (event.currentTarget.checked) {
						dispatch(selectAllFilteredRows());
					} else {
						dispatch(deselectAllFilteredRows());
					}
				}}
			/>
		);
	};

	const CardViewComponent = ({
		t,
		tableProps,
		selectedEntriesArray,
		isLoading,
		noDataChildren,
		onRowClick,
		cardActionFunc,
		dispatch,
		renderBreadcrumbs,
		isReadyToShare,
	}: any) => {
		return (
			<FilesGridCardView
				cardActionHandler={(event: any, data: any) => cardActionFunc(event, data)}
				dispatchSelect={(event: any, data: any) => {
					if (event.nativeEvent.shiftKey) {
						dispatch(
							selectRowsRange(data._id, [...(tableProps.selectedRows || [])].pop()),
						);
					} else if (event.currentTarget.checked) {
						dispatch(selectRow(data._id));
					} else {
						dispatch(deselectRow(data._id));
					}
				}}
				filesList={tableProps?.data}
				handleOpenFolder={(data: any) => onRowClick(data)}
				isLoading={isLoading}
				isReadyToShare={isReadyToShare}
				isSearchView={tableProps.searchView}
				noDataChildren={!isLoading && noDataChildren}
				searchBreadcrumbs={(cardItem: any) =>
					tableProps?.data?.map((file: any) => {
						return renderBreadcrumbs(file, cardItem);
					})
				}
				selectedEntriesArray={selectedEntriesArray}
				t={t}
			/>
		);
	};

	// MARK: Render Table
	const renderTable = () => {
		return (
			tableProps && (
				<div
					className={`bay-data-grid bay-files-grid ${
						tableProps.searchView ? 'search-mode' : ''
					} `}
				>
					<Table
						{...tableProps}
						childComponents={{
							headCell: {
								elementAttributes: (props: any) => {
									const hideBorder = [
										'selection-cell',
										'name',
										'dtUpd',
									].includes(props.column.key);
									return {
										style: {
											...props.column.style,
											borderRight: hideBorder
												? 'none'
												: '1px solid var(--bs-gray-200)',
											borderLeft: hideBorder
												? 'none'
												: '1px solid var(--bs-gray-200)',
										},
									};
								},
								content: (props: any) => {
									if (props.column.key === 'selection-cell') {
										return (
											<SelectionHeader
												{...props}
												areAllRowsSelected={kaPropsUtils.areAllFilteredRowsSelected(
													tableProps,
												)}
											/>
										);
									}
								},
							},
							cellEditor: {
								content: (props: any) => {
									const { dispatch, value, rowData } = props;
									const onSave = async (
										rowKeyValue: any,
										columnKey: string,
										updatedValue: string,
										rowData: any,
									) => {
										const newName = updatedValue;
										const oldName = value;
										try {
											await onEntryRename?.({
												newName,
												rowData,
												setCellValue: (value: string) => {
													dispatch(
														updateCellValue(rowKeyValue, columnKey, value),
													);
													dispatch(closeEditor(rowKeyValue, 'name'));
												},
											});
										} catch (error) {
											console.error('Error updating cell:', error);
											dispatch(
												updateCellValue(rowKeyValue, columnKey, oldName),
											);
											dispatch(closeEditor(rowKeyValue, 'name'));
										}
									};
									switch (props.column.key) {
									case SELECTION_COLUMN_ID:
										return <SelectionCell {...props} />;
									default:
										return (
											<CellEditor
												{...props}
												minWidth={
													rowData?._type === 'Entry$Folder' ? 40 : null
												}
												t={t}
												value={value}
												onSave={onSave}
											/>
										);
									}
								},
								elementAttributes: (props: any) => {
									return {
										style: {
											overflow: props.isEditableCell ? 'visible' : 'hidden',
										},
									};
								},
							},
							cellText: {
								elementAttributes: (props: any) => {
									switch (props.column.key) {
									case 'edit':
										return {
											style: {
												justifyContent: 'center',
											},
										};
									default:
										break;
									}
								},
								content: (props: any) => {
									const { dispatch, rowKeyValue, rowData } = props;

									const onClickOpenEditor = () => {
										dispatch(openEditor(rowKeyValue, 'name'));
									};

									const presentOnHomepageIcon = (rowData: any) => {
										return rowData.presentOnHomepage ? (
											<BsHouseHeartFill />
										) : (
											<BsHouse />
										);
									};

									const onChangeFunc = (eventKey: string, event: any, rowData: any) => {
										switch (eventKey) {
										case 'rename':
											onClickOpenEditor();
											return;
										case 'download':
											downloadURI(rowData.url, rowData.name);
											return;
										case 'delete':
											setShowDeleteModal(true, [rowData]);
											return;
										case 'copy':
											onEntryCopy([rowData]);
											return;
										case 'move':
											onEntryMove([rowData]);
											return;
										case 'preview':
											onPreview(rowData);
											return;
										case 'edit':
											onEditFolder(rowData);
											return;
										case 'editThumbnail':
											setShowEditThumbnailModal({ show: true, rowData });
											return;
										case 'visibility':
											onSetVisibility([rowData]);
											return;
										case 'homepage':
											onSetVisibility([rowData], 'homepage');
											return;
										default:
											return;
										}
									};

									const fileTypes =
										props.value === 'Entry$Folder'
											? 'Folder'
											: `${getFileColumnDataType(
												getFileType(props.rowData.mimeType),
											)}`;

									switch (props.column.key) {
									case 'selection-cell':
										return <SelectionCell {...props} />;
									case 'name':
										return (
											<div className="d-flex w-100 ka-cell-container">
												<div
													className={`w-100 ka-cell-text-container border-end ${
														tableProps.searchView ? 'with-search-result' : ''
													}`}
												>
													{tableProps.searchView &&
															tableProps?.data?.length > 0 && (
														<div
															className={`content ka-cell-breadcrumb ${
																tableProps.searchView ? 'pt-2 pb-1' : ''
															}`}
														>
															{tableProps?.data?.map((file: any, index: number) => {
																return (
																	<Fragment key={index}>
																		{renderBreadcrumbs(file, props.rowData)}
																	</Fragment>
																);
															})}
														</div>
													)}
													<div
														className="ka-cell-name-container d-flex align-items-center cursor-pointer"
														onClick={() => {
															onRowClick(props.rowData);
														}}
													>
														<div className="file-icon-container">
															{isThumbnailFile(
																props.rowData._type === 'Entry$File'
																	? 'file'
																	: 'folder',
															) ? (
																	<Image
																		key={props.rowData.name}
																		thumbnail
																		className="file-thumbnail-image"
																		lazyLoading
																		src={props.rowData.url}
																		onDragStart={(e) => e.preventDefault()}
																	/>
																) : (
																	getIconByFileType(
																		props.rowData._type === 'Entry$File'
																			? getFileType(props.rowData.mimeType)
																			: 'folder',
																	)
																)}
														</div>
														<div
															className={`${
																section ? section : 'default-section'
															} content ms-1`}
														>
															<BayTooltip
																overlay={
																	props.rowData._type === 'Entry$Folder'
																		? t('open')
																		: props.value
																}
																placement="top"
															>
																<div
																	className={`content mb-1 text-truncate ${props.rowData.isHidden ? 'text-500' : ''}`}
																>
																	{props.value}
																</div>
															</BayTooltip>
														</div>
													</div>
												</div>
												<div className="d-flex ka-cell-action-container">
													<div className="px-2 d-flex align-items-center">
														<div
															className={`col-action-button text-primary ${
																props.rowData._type === 'Entry$File' &&
!disableEdit
																	? ''
																	: 'cursor-pointer'
															}`}
														>
															<EditCell
																disableEdit={disableEdit}
																isAllowedToPreview={isPreviewFile(
																	getFileType(props.rowData.mimeType),
																)}
																isReadyToShare={isReadyToShare}
																isVideoFile={
																	getFileType(props.rowData.mimeType) ===
																			'mp4' ||
																		getFileType(props.rowData.mimeType) ===
																			'mov'
																}
																rowData={props.rowData}
																t={t}
																onChangeFunc={onChangeFunc}
															/>
														</div>
													</div>
												</div>
											</div>
										);
									case 'column3':
										return (
											<div
												className={`d-flex justify-content-${isReadyToShare ? 'evenly' : 'center'} align-items-center w-100 ${props.rowData.isHidden ? 'text-500' : 'text-700'}`}
											>
												<BayTooltip
													overlay={
														props.rowData.isHidden ? t('hidden') : t('shown')
													}
													placement="top"
												>
													<span
														className={`${isReadyToShare ? 'me-2' : 'me-0'}`}
													>
														<VisibilityIcon
															isHidden={props.rowData.isHidden}
														/>
													</span>
												</BayTooltip>
												{isReadyToShare &&
														props.rowData._type === 'Entry$Folder' && (
													<BayTooltip
														overlay={
															props.rowData.presentOnHomepage
																? t('appearsOnHomePage')
																: t('hiddenFromHomePage')
														}
														overlayMaxWidth={325}
														placement="top"
													>
														<span className="present-on-homepage-icon">
															{presentOnHomepageIcon(props.rowData)}
														</span>
													</BayTooltip>
												)}
											</div>
										);

									case 'size':
										return rowData._type === 'Entry$File' ? (
											getBytes(props.value)
										) : (
											<></>
										);
									case '_type':
										return (
											<BayTooltip overlay={fileTypes} placement="top">
												<div
													className={`text-truncate ${props.rowData.isHidden ? 'text-500' : ''}`}
												>
													{fileTypes}
												</div>
											</BayTooltip>
										);
									case 'dtUpd':
										return (
											<div
												className={`text-truncate ${props.rowData.isHidden ? 'text-500' : ''}`}
											>
												<div className="last-updated-date-time">
													{props?.value === ' '
														? ''
														: dayjs(props?.value).format(
															'MMMM DD, YYYY [at] hh:mm:ss',
														)}
													<br />
												</div>
												<div className="last-updated-date">
													{props?.value === ' '
														? ''
														: dayjs(props?.value).format('MM/DD/YYYY')}
												</div>
											</div>
										);
									}
								},
							},
							sortIcon: {
								content: ({ column }: any) => {
									return (
										<span
											style={{
												width: 12,
												position: 'relative',
												top: 1,
												paddingRight: '0.5rem',
											}}
										>
											{column.sortDirection === SortDirection.Ascend ? (
												<BsSortDown color="var(--bs-gray-500)" size={16} />
											) : (
												<BsSortUp color="var(--bs-gray-500)" size={16} />
											)}
										</span>
									);
								},
							},
							noDataRow: {
								content: ({ loading, noData }: any) => {
									return loading?.enabled ? (
										<></>
									) : searchValue && searchValue !== '' ? (
										noData?.text
									) : (
										noDataChildren
									);
								},
							},
							loading: {
								content: () => {
									return <BaySpinner />;
								},
							},
						}}
						columns={tableProps.columns ?? []}
						dispatch={dispatch}
						extendedSort={(data: any[], columns: any[]) => {
							const folders = data.filter(
								(data) => data._type === 'Entry$Folder',
							);
							const files = data.filter((data) => data._type === 'Entry$File');
							let sortedColumns = columns.filter((c) => c.sortDirection);
							if (sortedColumns.length === 0) {
								return [...folders, ...files];
							}
							sortedColumns = orderBy(sortedColumns, ['sortIndex'], ['asc']);
							const iteratee = sortedColumns?.map((c) => c.key);
							const order = sortedColumns?.map((c) =>
								c.sortDirection === SortDirection.Ascend ? 'asc' : 'desc',
							);
							const foldersSorting = orderBy(folders, iteratee, order as any);
							const filesSorting = orderBy(files, iteratee, order as any);
							return [...foldersSorting, ...filesSorting];
						}}
						loading={{
							enabled: isLoading,
						}}
						sortingMode={SortingMode.Single}
					/>
				</div>
			)
		);
	};

	// Handler for showing the delete thumbnail modal
	const handleShowDeleteThumbnailModal = (fileName: string, entry: any) => {
		setThumbnailToDelete(fileName);
		setShowDeleteThumbnailModal({ show: true, rowData: entry });
	};

	return (
		<>
			{tableProps.selectedRows &&
			tableProps.selectedRows.length > 0 &&
			!isLoading ? (
					<div className="my-4">
						{selectedEntriesArray?.length > 0 && (
							<BayBulkActions
								bulkActions={bulkActions}
								hideBulkActions={hideBulkActions}
								selectedData={selectedEntriesArray}
								showMoreAction={false}
								t={t}
								onActionClick={handleBulkAction}
							/>
						)}
					</div>
				) : (
					<>
						<BayDataGridActionsBar
							filterCount={filterCount}
							hideBulkActions={hideBulkActions}
							isLoading={isLoading}
							isSwitchOn={isSwitchOn}
							SearchComponent={
								<BaySearch
									disabled={isLoading}
									handleSearchInput={debounceSearch}
									hideMenu
									id="bay_files_search_input"
									loading={isLoading}
									name="bay_files_search"
									noOptionItemText="entries"
									optionsArray={dataArray?.map((data) => {
										return {
											value: data.name,
											label: data.name,
										};
									})}
									placeholder="Search..."
									searchInputValue={searchValue}
									showNoOptionLink={false}
									t={t}
									onChangeFunc={(e: any) => debounceSearch(e?.target?.value ?? '')}
									onKeyDownFunc={(e: React.KeyboardEvent) => {
										if (e.key === 'Enter' || e.keyCode === 13) {
											e.stopPropagation();
											e.preventDefault();

											debounceSearch((e.target as HTMLInputElement).value);
										}
									}}
								/>
							}
							setIsSwitchOn={setIsSwitchOn}
							setModal={setShowFilterDrawer}
							showFilterButton={showFilterButton}
							t={t}
						/>
						<div className="breadcrumb-section pb-4 lh-1">
							{breadCrumbsArray?.length >= 1 && (
								<div
									className={
										tableProps.selectedRows && tableProps.selectedRows.length > 0
											? 'disable-breadcrumbs'
											: ''
									}
								>
									<BayBreadcrumbs
										breadcrumbs={breadCrumbsArray}
										onSelect={handleBreadcrumbClick}
									/>
								</div>
							)}
						</div>
					</>
				)}

			<div
				className={`bay-files-grid-container ${
					section ? section : 'default-section'
				}`}
			>
				<div
					className={`${isLoading && isSwitchOn ? 'bay-placeholder-overlay' : ''}`}
				>
					{!disableUpload && onDrop ? (
						<BayFilesUpload
							allowedFileTypes={allowedFileTypes}
							allowedFileTypesErrorText={allowedFileTypesErrorText}
							allowedFileTypesText={allowedFileTypesText}
							classNamesArray={
								!isSwitchOn && tableProps?.data?.length <= 0
									? ['bg-white', 'px-5', 'py-4', 'shadow']
									: []
							}
							isFilesGrid
							isMultiple={multiFileUpload}
							noData={tableProps?.data?.length <= 0}
							t={t}
							uploadingPopUpTitle={uploadingPopUpTitle}
							useTableView={isSwitchOn}
							onDrop={onDrop}
						>
							{isSwitchOn ? (
								renderTable()
							) : (
								<CardViewComponent
									cardActionFunc={cardActionFunc}
									dispatch={dispatch}
									isLoading={isLoading}
									isReadyToShare={isReadyToShare}
									noDataChildren={noDataChildren}
									renderBreadcrumbs={renderBreadcrumbs}
									selectedEntriesArray={selectedEntriesArray}
									t={t}
									tableProps={tableProps}
									onRowClick={onRowClick}
								/>
							)}
						</BayFilesUpload>
					) : (
						<>
							{isSwitchOn ? (
								renderTable()
							) : (
								<CardViewComponent
									cardActionFunc={cardActionFunc}
									dispatch={dispatch}
									isLoading={isLoading}
									isReadyToShare={isReadyToShare}
									noDataChildren={noDataChildren}
									renderBreadcrumbs={renderBreadcrumbs}
									selectedEntriesArray={selectedEntriesArray}
									t={t}
									tableProps={tableProps}
									onRowClick={onRowClick}
								/>
							)}
						</>
					)}
				</div>
			</div>

			{showFilterDrawer && (
				<FilterDrawer
					fileTypes={filterFileTypes}
					setShowFilterDrawer={setShowFilterDrawer}
					showFilterDrawer={showFilterDrawer}
					t={t}
					onSubmit={handleFilterSubmit}
				/>
			)}

			{showCreateFolderModal && (
				<CreateNewFolderModal
					buttonText={t('create')}
					isReadyToShare={isReadyToShare}
					showModal={showCreateFolderModal}
					t={t}
					title={t('createNewFolder')}
					onCloseFunc={() => {
						setCreateFolderModal(false);
					}}
					onSubmitFunc={(data: any) => createFolderSubmitFunc(data)}
				/>
			)}
			{showEditFolderModal && editFolderData && (
				<CreateNewFolderModal
					buttonText={t('save')}
					editData={editFolderData}
					isEditMode
					isReadyToShare={isReadyToShare}
					showModal={showEditFolderModal}
					t={t}
					title={t('editFolder')}
					onCloseFunc={() => {
						setShowEditFolderModal(false);
					}}
					onSubmitFunc={(data: any) => editFolderSubmitFunc(data)}
				/>
			)}

			{showEditEntryModal && (
				<EditEntryModal
					buttonText={t('save')}
					editModalType={
						selectedEntriesArray[0]?._type === 'Entry$Folder'
							? 'folder'
							: 'file'
					}
					selectedFile={selectedEntriesArray[0]}
					showModal={showEditEntryModal}
					t={t}
					title={
						selectedEntriesArray[0]?._type === 'Entry$Folder'
							? t('renameFolder')
							: t('renameFile')
					}
					onCloseFunc={() => {
						setEditEntryModal(false);
					}}
					onSubmitFunc={(dataObj: any) => editEntrySubmitFunc(dataObj)}
				/>
			)}

			{showPreviewFileModal && (
				<PreviewFileModal
					isLoading={isLoading}
					mimetype={showPreviewFileModal?.rowData?.mimeType ?? ''}
					previewUrl={showPreviewFileModal?.rowData?.url ?? ''}
					setShowPreviewFileModal={() => setShowPreviewFileModal(false)}
					showPreviewFileModal={showPreviewFileModal?.show}
					t={t}
					thumbnailUrl={
						showPreviewFileModal?.rowData?.thumbnailUrls?.custom ||
						showPreviewFileModal?.rowData?.thumbnailUrls?.default
					}
					title={showPreviewFileModal?.rowData?.name ?? ''}
				/>
			)}

			{showDeleteModal && (
				<DeleteEntryModal
					bodyText={deleteBodyText}
					isLoading={isLoading}
					setShowDeleteEntryModal={setShowDeleteModal}
					showDeleteEntryModal={showDeleteModal}
					submitButtonTitle={t('deleteMessage')}
					t={t}
					title={deleteTitleText}
					onSubmitFunc={(selectedEntriesArray: any[]) =>
						onEntryDelete(selectedEntriesArray)
					}
				/>
			)}

			{showMoveModal && (
				<MoveEntryModal
					currentEntry={currentEntry}
					foldersArray={dataArray.filter(
						(data) => data._type === 'Entry$Folder',
					)}
					handleCreateFolder={(data: any) => createFolderSubmitFunc(data)}
					isActionLoading={isActionLoading}
					isReadyToShare={isReadyToShare}
					modalType={isCopy ? 'copyTo' : 'moveTo'}
					moveModalSubmitFunc={moveModalSubmitFunc}
					selectedRowData={selectedEntriesArray}
					showMoveModal={showMoveModal}
					t={t}
					onCloseFunc={() => {
						setShowMoveModal(false);
					}}
					onNavigate={getEntries}
				/>
			)}

			{showEditThumbnailModal?.show && (
				<EditThumbnailModal
					file={showEditThumbnailModal?.rowData}
					isLoading={isLoading || isActionLoading}
					setShowModal={setShowEditThumbnailModal}
					showModal={showEditThumbnailModal}
					t={t}
					onRequestDelete={handleShowDeleteThumbnailModal}
					onSave={onThumbnailEdit}
				/>
			)}

			{showDeleteThumbnailModal && (
				<DeleteThumbnailModal
					fileName={thumbnailToDelete}
					isLoading={isLoading || isActionLoading}
					setShowDeleteThumbnailModal={setShowDeleteThumbnailModal}
					showDeleteThumbnailModal={showDeleteThumbnailModal}
					t={t}
					onDelete={onThumbnailDelete}
				/>
			)}

			{showUploadFileModal && (
				<BayFilesProgressModal
					allowedFileTypes={allowedFileTypes}
					allowedFileTypesText={allowedFileTypesText}
					apiConfig={apiConfig}
					enforceVideoConstraints={enforceVideoConstraints}
					formDataObjectName={formDataObjectName ? formDataObjectName : undefined}
					handleModalClose={setShowUploadFileModal}
					shouldGenerateThumbnails={shouldGenerateThumbnails}
					showUploadingFilesModal={showUploadFileModal}
					t={t}
					thumbnailFiles={thumbnailFiles}
					uploadingFiles={uploadingFiles}
				/>
			)}
		</>
	);
};

export default BayFilesGrid;
